import React, { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LoginForm from "@/components/auth/LoginForm";
import RegisterForm from "@/components/auth/RegisterForm";
import { useAuth } from "@/providers/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import { 
  Sparkles, 
  ArrowLeft, 
  User, 
  Lock, 
  Mail,
  Palette,
  Award,
  Shield,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Login = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");

  // SEO: noindex private/auth page, set canonical
  useSEO({
    title: "Login",
    description: "Access your Florida Highwaymen Auction account.",
    canonicalUrl: "/login",
    noIndex: true,
    ogType: 'website'
  });

  // If user is already logged in, redirect them to their intended destination or home
  useEffect(() => {
    if (user) {
      const redirectUrl = redirect || "/";
      navigate(redirectUrl);
    }
  }, [user, redirect, navigate]);

  useEffect(() => {
    const handleOpenRegister = () => setActiveTab('register');

    window.addEventListener('auth:open-register', handleOpenRegister);
    return () => {
      window.removeEventListener('auth:open-register', handleOpenRegister);
    };
  }, []);

  // Don't render the login form if user is already logged in
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Animated gradient orbs */}
        <motion.div 
          className="absolute top-1/4 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-indigo-500/10 to-transparent rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-transparent rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='0.5' opacity='0.5'%3E%3Cpath d='M0 30h60M30 0v60'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Navigation Header */}
      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-white font-light text-lg hidden sm:inline">Florida Highwaymen Auction</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-5xl"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Hero Content */}
            <div className="hidden lg:block">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className="text-5xl xl:text-6xl font-light text-white mb-6">
                  Welcome to the
                  <span className="block font-normal bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent">
                    Premier Auction Platform
                  </span>
                </h1>
                <p className="text-xl text-white/70 font-light mb-8 leading-relaxed">
                  Join collectors worldwide in discovering and acquiring authentic Florida Highwaymen masterpieces.
                </p>
                
                {/* Feature Cards */}
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10"
                  >
                    <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Authenticated Artworks</h3>
                      <p className="text-white/60 text-sm">Every piece verified by experts</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10"
                  >
                    <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Zap className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Live Bidding</h3>
                      <p className="text-white/60 text-sm">Real-time auction experience</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10"
                  >
                    <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Award className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Exclusive Collection</h3>
                      <p className="text-white/60 text-sm">Curated masterpieces</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Right Side - Auth Forms */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8">
                {redirect && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-blue-500/10 backdrop-blur-sm rounded-xl border border-blue-500/20"
                  >
                    <p className="text-blue-300 text-sm font-light">
                      {redirect === '/presentation' 
                        ? "Sign in to continue to the live presentation"
                        : "Sign in to continue where you left off"
                      }
                    </p>
                  </motion.div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-1">
                    <TabsTrigger 
                      value="login"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white text-white/70 rounded-lg transition-all duration-300"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="register"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white text-white/70 rounded-lg transition-all duration-300"
                    >
                      Register
                    </TabsTrigger>
                  </TabsList>
                  
                  <AnimatePresence mode="wait">
                    <TabsContent value="login" className="mt-8">
                      <motion.div
                        key="login"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="space-y-2 mb-6">
                          <h2 className="text-3xl font-light text-white">Welcome Back</h2>
                          <p className="text-white/60 font-light">Enter your credentials to access your account</p>
                        </div>
                        <LoginForm redirectUrl={redirect} />
                      </motion.div>
                    </TabsContent>
                    
                    <TabsContent value="register" className="mt-8">
                      <motion.div
                        key="register"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="space-y-2 mb-6">
                          <h2 className="text-3xl font-light text-white">Create Account</h2>
                          <p className="text-white/60 font-light">Join our community of art collectors</p>
                        </div>
                        <RegisterForm redirectUrl={redirect} />
                      </motion.div>
                    </TabsContent>
                  </AnimatePresence>
                </Tabs>

                {/* Social Login Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-transparent text-white/50 font-light">Or continue with</span>
                  </div>
                </div>

                {/* Demo Account Info */}
                <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                  <p className="text-white/70 text-sm font-light text-center">
                    Demo Account: <span className="text-emerald-400">demo@example.com</span> / <span className="text-emerald-400">password</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Floating decorative elements */}
      <div className="absolute bottom-10 left-10 w-20 h-20 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-2xl animate-pulse" />
      <div className="absolute top-1/3 right-10 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
    </div>
  );
};

export default Login;